import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingOptions,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    LineTypes,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getDistanceBetweenPoints, getDistanceFromLine, scaledCoordinate } from '../drawing.helper'
import { primaryDrawingColor } from '../../../../assets/colors'

export type LineDrawingType = 'trendLine' | 'arrow' | 'ray' | 'extendedLine' | 'trendAngle' | 'infoLine' | 'simpleLine'
export type LineHeadType = 'arrow' | 'normal'

export type LineDrawingState = {
    p1?: Point,
    p2?: Point
}

export type LineToolOptions = {
    drawingType: LineDrawingType
}

export type LineDrawingSettings = {
    lineType: LineTypes,
    lineColor: ColorValue,
    lineWidth: number,
    point1: Point,
    point2: Point,
    extendLeft: boolean,
    extendRight: boolean,
    leftHeadType: LineHeadType,
    rightHeadType: LineHeadType,
    statsPosition: string,
    showStats: boolean
}

const THRESHOLD = 10 // cursor distance threshold for treating as hovered or not

class LineDrawingTool extends DrawingBase<LineToolOptions, LineDrawingSettings, LineDrawingState> {
    slope: number = 0
    rotation = 0
    intercept = 0
    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0
    }
    barStatsImage?: HTMLImageElement
    priceStatsImage?: HTMLImageElement

    constructor(options: DrawingOptions<LineToolOptions, LineDrawingSettings, LineDrawingState>) {
        super(options)

        const barStatsBlob = new Blob([this.getBarStatsSvg()], { type: 'image/svg+xml' })
        const barStatsUrl = URL.createObjectURL(barStatsBlob)

        const barImage = document.createElement('img')
        barImage.onload = () => {
            this.barStatsImage = barImage
        }
        barImage.src = barStatsUrl
        const priceStatsBlob = new Blob([this.getPriceStatsSvg()], { type: 'image/svg+xml' })
        const priceStatsUrl = URL.createObjectURL(priceStatsBlob)

        const priceImage = document.createElement('img')
        priceImage.onload = () => {
            this.priceStatsImage = priceImage
        }
        priceImage.src = priceStatsUrl
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        let start = this.getStartCoordinate()
        let end = this.getEndCoordinate()
        if (!start || !end) return
        ctx.beginPath()
        this.setLineAttributes(ctx)
        ctx.moveTo(start.x, start.y)
        ctx.lineTo(end.x, end.y)
        ctx.stroke()
        ctx.closePath()
        if (this._settings.leftHeadType !== 'normal') {
            this.drawLineHead(ctx, true, this._settings.leftHeadType)
        }
        if (this._settings.rightHeadType !== 'normal') {
            this.drawLineHead(ctx, false, this._settings.rightHeadType)
        }
        if (this.options.drawingType === 'trendAngle') {
            this.drawAngle(ctx)
        }
        if (this.options.drawingType === 'infoLine' && this._settings.showStats) {
            this.renderStats(ctx)
        }
    };

    calculateLineAttributes() {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates = { p1x, p1y, p2x, p2y }
            this.slope = (p2y - p1y) / (p2x - p1x)
            this.intercept = p1y - this.slope * p1x
            this.rotation = Math.atan(this.slope)
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        const lineWidth = this._settings.lineWidth * window.devicePixelRatio
        if (this._settings.lineType === 'dashed') {
            lineDash = [Math.max(this._settings.lineWidth * 3, 8), 3]
        } else if (this._settings.lineType === 'dotted') {
            lineDash = [lineWidth, lineWidth]
        }
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    drawLineHead(ctx: CanvasRenderingContext2D, left: boolean, type: LineHeadType) {
        let { p1x, p2x, p1y, p2y } = this.scaledCoordinates
        if (type === 'arrow') {
            const sign = (left && p1x < p2x) || (!left && p1x >= p2x) ? 1 : -1

            const head = Math.max(10, (this._settings.lineWidth * window.devicePixelRatio) * 4)

            let angle1 = Math.PI / 4 - this.rotation
            if (Math.abs(this.rotation) === Math.PI / 2) {
                angle1 = this.rotation + Math.PI / 4
            }
            let x = left ? p1x : p2x
            let y = left ? p1y : p2y
            const head1x = x + sign * head * Math.cos(angle1)
            const head1y = y - sign * head * Math.sin(angle1)
            this.setLineAttributes(ctx, false)
            ctx.strokeStyle = primaryDrawingColor
            ctx.setLineDash([])
            ctx.beginPath()
            ctx.moveTo(x, y)
            ctx.lineTo(head1x, head1y)
            ctx.stroke()
            ctx.closePath()

            let angle2 = Math.PI / 4 + this.rotation
            if (Math.abs(this.rotation) === Math.PI / 2) {
                angle2 = -this.rotation + Math.PI / 4
            }
            const head2x = x + sign * head * Math.cos(angle2)
            const head2y = y + sign * head * Math.sin(angle2)
            ctx.strokeStyle = primaryDrawingColor
            ctx.beginPath()
            ctx.moveTo(x, y)
            ctx.lineTo(head2x, head2y)
            ctx.stroke()
            ctx.closePath()
        }
    }

    drawAngle(ctx: CanvasRenderingContext2D) {
        let { p1x, p2x, p1y, p2y } = this.scaledCoordinates

        const lineLength = getDistanceBetweenPoints(p1x, p2x, p1y, p2y)
        const radius = Math.min(lineLength * 0.5, 40)
        let rotation = this.rotation


        const sign = -rotation / Math.abs(rotation)

        if (p1x > p2x) {
            rotation = sign * (Math.PI - Math.abs(rotation))
        }
        ctx.lineWidth = window.devicePixelRatio
        ctx.strokeStyle = this._settings.lineColor.color
        ctx.setLineDash([1, 2])
        ctx.beginPath()
        ctx.arc(p1x, p1y, radius, 0, rotation, rotation < 0)
        ctx.stroke()
        ctx.closePath()

        ctx.beginPath()
        ctx.moveTo(p1x, p1y)
        ctx.lineTo(p1x + radius, p1y)
        ctx.stroke()
        ctx.closePath()

        this.renderAngleText(ctx, rotation, radius)
    }

    renderAngleText(ctx: CanvasRenderingContext2D, angle: number, arcRadius: number) {
        const sign = angle / Math.abs(angle)
        const text = ((-1) * angle * 180 / Math.PI).toFixed(1)
        ctx.font = `${12 * window.devicePixelRatio}px sans-serif`
        ctx.fillStyle = this._settings.lineColor.color
        ctx.textBaseline = 'top'
        let { p1x, p1y } = this.scaledCoordinates
        const textX = p1x + arcRadius + 4
        const textY = p1y - sign * 2
        ctx.fillText(text, textX, textY)
        const dim = ctx.measureText(text)
        const textWidth = dim.width
        ctx.font = `${8 * window.devicePixelRatio}px sans-serif`
        ctx.fillText('o', textX + textWidth + 1, textY)
    }

    renderStats(ctx: CanvasRenderingContext2D) {
        const { p1, p2 } = this._state
        if (!this.barStatsImage || !this.priceStatsImage || !p1 || !p2) return
        const devicePixelRatio = window.devicePixelRatio
        const padding = 12 * devicePixelRatio, imageSize = 16 * devicePixelRatio,
            verticalSpacing = 12 * devicePixelRatio
        ctx.lineWidth = 0.5
        ctx.font = `${12 * devicePixelRatio}px sans-serif`
        const numberOfBars = this.getBarRange()
        let boundingWidth = 0, boundingHeight = 0

        const barText = `${numberOfBars} bars`
        const [change, percent] = this.getPriceRange()
        const priceText = `${change.toFixed(2)} (${percent.toFixed(2)} %)`
        let priceTextDimensions = ctx.measureText(priceText)
        const priceTextHeight = priceTextDimensions.fontBoundingBoxAscent + priceTextDimensions.fontBoundingBoxDescent
        boundingHeight += priceTextHeight + verticalSpacing
        boundingWidth = Math.max(boundingWidth, priceTextDimensions.width)

        let barTextDimensions = ctx.measureText(barText)
        boundingWidth = Math.max(boundingWidth, barTextDimensions.width)
        const barTextHeight = barTextDimensions.fontBoundingBoxAscent + barTextDimensions.fontBoundingBoxDescent
        boundingHeight += barTextHeight

        boundingWidth += 3 * padding + imageSize
        boundingHeight += 2 * padding

        const [startX, startY] = this.getStatsPosition(this._settings.statsPosition, boundingWidth, boundingHeight)

        ctx.strokeStyle = '#E6F3FD'
        ctx.globalAlpha = 1
        ctx.roundRect(startX, startY, boundingWidth, boundingHeight, 2)
        ctx.stroke()
        ctx.fillStyle = '#E6F3FD'
        ctx.globalAlpha = 0.7
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.fillStyle = '#000'
        ctx.drawImage(this.priceStatsImage, startX + padding, startY + padding, imageSize, imageSize)
        ctx.textBaseline = 'top'
        ctx.fillText(priceText, startX + 2 * padding + imageSize, startY + padding)
        ctx.drawImage(this.barStatsImage, startX + padding, startY + padding + barTextHeight + verticalSpacing, imageSize, imageSize)
        ctx.textBaseline = 'top'
        ctx.fillText(barText, startX + 2 * padding + imageSize, startY + padding + barTextHeight + verticalSpacing)
    }

    getStatsPosition(statsPosition: string, boundingWidth: number, boundingHeight: number): [number, number] {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return [0, 0]
        let { p1x, p1y, p2x, p2y } = this.scaledCoordinates

        let startX = 0, startY = 0, offset = 10
        let pointX = p1x, pointY = p1y

        if (statsPosition === 'right') {
            pointX = p2x
            pointY = p2y
        } else if (statsPosition === 'center') {
            pointX = (p1x + p2x) / 2
            pointY = (p1y + p2y) / 2
        }

        startX = pointX + offset
        if (this.slope <= 0) {
            startY = pointY + offset
        } else {
            startY = pointY - offset - boundingHeight
        }

        if (startY + boundingHeight > this.canvasHeight || startX + boundingWidth > this.canvasWidth) {
            startX = pointX - offset - boundingWidth
            if (this.slope >= 0) {
                startY = pointY + offset
            } else {
                startY = pointY - offset - boundingHeight
            }
        }

        return [startX, startY]
    }

    getBarRange(): number {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return 0

        const index1 = this.timeToLogical(p1.x)
        const index2 = this.timeToLogical(p2.x)

        if (!index1 || !index2) return 0
        let max = index2, min = index1

        if (max < min) {
            max = index1
            min = index2
        }
        return max - min
    }

    // returns difference and percentage
    getPriceRange(): [number, number] {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return [0, 0]
        const diff = p2.y - p1.y

        return [diff, diff * 100 / Math.abs(p1.y)]
    }

    getStartCoordinate(): PointCoordinates | void {
        let { p1x, p2x, p1y } = this.scaledCoordinates

        let start: PointCoordinates = {
            x: p1x,
            y: p1y
        }

        if (this._settings.extendLeft) {
            let y = 0
            if ((this.slope < 0 && p1x < p2x) || (this.slope > 0 && p1x > p2x)) {
                y = this.canvasHeight
            }
            start.y = y
            start.x = (y - this.intercept) / this.slope
        }
        return start
    }

    getEndCoordinate(): PointCoordinates | void {
        let { p1x, p2x, p2y } = this.scaledCoordinates

        let end: PointCoordinates = {
            x: p2x,
            y: p2y
        }


        if (this._settings.extendRight) {
            let y = 0
            if ((this.slope < 0 && p1x > p2x) || (this.slope > 0 && p1x < p2x)) {
                y = this.canvasHeight
            }
            end.y = y
            end.x = (y - this.intercept) / this.slope

        }

        return end
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const { p1, p2 } = this._state
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!p1) {
            this._state.p1 = point
        } else if (!p2 || preview) {
            point.id = 'p2'
            this._state.p2 = point
        }
        p2 && !preview && this.onDrawingEnd()
        if (!preview) return { ...point }
    }

    recalculateHandles = () => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return

        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)
        const p1y = this.priceToCoordinate(p1.y)
        const p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.slope = (p2y - p1y) / (p2x - p1x)
            this.intercept = p1y - this.slope * p1x
            this.rotation = Math.atan(this.slope)
        }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state
        if (p1) {
            handles.push(p1)
        }

        p2 && handles.push(p2)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const start = this.getStartCoordinate()
        const end = this.getEndCoordinate()
        if (!start || !end) return null

        let minX = start.x, maxX = end.x, minY = start.y, maxY = end.y

        if (maxX < minX) {
            minX = end.x
            maxX = start.x
        }

        if (maxY < minY) {
            minY = end.y
            maxY = start.y
        }

        // return null if cursor is outside points  + threshold
        if (scaledX < minX - THRESHOLD / 3 || scaledX > maxX + THRESHOLD / 3 || scaledY < minY - THRESHOLD / 3 || scaledY > maxY + THRESHOLD / 3) return null
        const distance = getDistanceFromLine(scaledX, scaledY, this.slope, this.intercept)
        // make cursor active if distance from the line is less than a threshold
        if (distance <= THRESHOLD) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h

            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return
        switch (id) {
            case 'p1':
                p1.x = time
                p1.y = price
                break
            case 'p2':
                p2.x = time
                p2.y = price
                break
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2 || !Object.keys(this._settings).length) return []
        const min = Math.min(p1.y, p2.y)
        const max = Math.max(p1.y, p2.y)
        if (!min || !max) return []
        return [{
            id: 'price',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2 || !Object.keys(this._settings).length) return []
        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)

        if (!p1x || !p2x) return []
        let min: Time = p1.x, max: Time = p2.x
        if (p1x > p2x) {
            min = p2.x
            max = p1.x
        }
        if (!min || !max) return []

        return [{
            id: 'time',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    onSettingsUpdate = (settings: Partial<LineDrawingSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
        }
        if (settings.point2) {
            this._state.p2 = settings.point2
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<LineDrawingSettings, SettingValues>[] => {
        const { drawingType } = this.options
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineType',
                    label: 'Line Type',
                    type: SettingType.LINE_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                },
                {
                    name: 'extendLeft',
                    label: 'Extend Left',
                    type: SettingType.BOOLEAN,
                    defaultValue: ['ray', 'extendedLine'].indexOf(drawingType) !== -1,
                    custom: true,
                    visible: ['ray', 'extendedLine'].indexOf(drawingType) !== -1
                },
                {
                    name: 'extendRight',
                    label: 'Extend Right',
                    type: SettingType.BOOLEAN,
                    defaultValue: drawingType === 'extendedLine',
                    custom: true,
                    visible: ['ray', 'extendedLine'].indexOf(drawingType) !== -1
                },
                {
                    name: 'leftHeadType',
                    label: 'Left Head',
                    type: SettingType.LINE_HEAD_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'rightHeadType',
                    label: 'Right Head',
                    type: SettingType.LINE_HEAD_TYPE,
                    defaultValue: ['ray', 'arrow'].indexOf(drawingType) !== -1 ? 'arrow' : 'normal',
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'stats',
            displayName: 'Stats',
            visible: drawingType === 'infoLine',
            settings: [
                {
                    name: 'statsPosition',
                    label: 'Stats Position',
                    type: SettingType.SELECT,
                    defaultValue: 'center',
                    options: [
                        { label: 'Center', value: 'center' },
                        { label: 'Left', value: 'left' },
                        { label: 'Right', value: 'right' }
                    ],
                    custom: true,
                    visible: true
                },
                {
                    name: 'showStats',
                    label: 'Show Stats',
                    type: SettingType.BOOLEAN,
                    defaultValue: drawingType === 'infoLine',
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: true
                    }
                },
                {
                    name: 'point2',
                    label: '#2 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p2',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: true
                    }
                }
            ]
        }]
    }

    getSettingsValues(): LineDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        if (this._state.p2) {
            settings.point2 = this._state.p2
        }
        return settings
    }

    getDrawingType(): DrawingTypes {

        switch (this.options.drawingType) {
            case 'trendLine':
                return 'trendLine'
            case 'arrow':
                return 'arrow'
            case 'extendedLine':
                return 'extendedLine'
            case 'ray':
                return 'ray'
            case 'trendAngle':
                return 'trendAngle'
            case 'infoLine':
                return 'infoLine'
            case 'simpleLine':
                return 'simpleLine'

        }
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineType', 'lineWidth'] as (keyof LineDrawingSettings)[]
    }

    getBarStatsSvg() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" x="0px" y="0px" viewBox="0 0 100 125" style="enable-background:new 0 0 100 100;" xml:space="preserve">
                <path d="M79.7,48.2c1,1,1,2.6,0,3.5L68.4,63c-0.5,0.5-1.1,0.7-1.8,0.7s-1.3-0.2-1.8-0.7c-1-1-1-2.6,0-3.5l7-7h-12H40.1h-12l7,7  c1,1,1,2.6,0,3.5c-0.5,0.5-1.1,0.7-1.8,0.7S32,63.5,31.6,63L20.3,51.8c-1-1-1-2.6,0-3.5L31.6,37c1-1,2.6-1,3.5,0c1,1,1,2.6,0,3.5  l-7,7h12h19.8h12l-7-7c-1-1-1-2.6,0-3.5c1-1,2.6-1,3.5,0L79.7,48.2z M7.5,5C6.1,5,5,6.1,5,7.5v85C5,93.9,6.1,95,7.5,95  s2.5-1.1,2.5-2.5v-85C10,6.1,8.9,5,7.5,5z M92.5,5C91.1,5,90,6.1,90,7.5v85c0,1.4,1.1,2.5,2.5,2.5s2.5-1.1,2.5-2.5v-85  C95,6.1,93.9,5,92.5,5z"/>
            </svg>
        `
    }

    getPriceStatsSvg() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" x="0px" y="0px" viewBox="0 0 100 125" style="enable-background:new 0 0 100 100;" xml:space="preserve">
                <path d="M52.5,59.9v12l7-7c1-1,2.6-1,3.5,0s1,2.6,0,3.5L51.8,79.7c-0.5,0.5-1.1,0.7-1.8,0.7s-1.3-0.2-1.8-0.7L37,68.4  c-1-1-1-2.6,0-3.5c1-1,2.6-1,3.5,0l7,7v-12V40.1v-12l-7,7c-1,1-2.6,1-3.5,0c-1-1-1-2.6,0-3.5l11.2-11.2c1-1,2.6-1,3.5,0L63,31.6  c1,1,1,2.6,0,3.5c-0.5,0.5-1.1,0.7-1.8,0.7s-1.3-0.2-1.8-0.7l-7-7v12V59.9z M92.5,90h-85C6.1,90,5,91.1,5,92.5S6.1,95,7.5,95h85  c1.4,0,2.5-1.1,2.5-2.5S93.9,90,92.5,90z M7.5,10h85c1.4,0,2.5-1.1,2.5-2.5S93.9,5,92.5,5h-85C6.1,5,5,6.1,5,7.5S6.1,10,7.5,10z"/>
            </svg>
        `
    }
}

export default LineDrawingTool